import { Error, Loading } from '@hogwarts/ui-components-core';
import { orderBy } from 'lodash';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPreferences as UserPreferencesComponent } from '../../../components/Settings/UserPreferences';
import { AnalyticsContext, UserContext } from '../../../context';
import { useMutation, useQuery, useQueryResults } from '../../../hooks';
import { UPDATE_MY_PASSWORD, UPDATE_USER_PREFERENCES } from '../../../mutations';
import {
  GET_ALL_LANGUAGES,
  GET_CURRENT_USER,
  GET_LOCATIONS,
  GET_TIMEZONES,
  GET_USER_ORGANISATIONS,
} from '../../../queries';
import authenticationService from '../../../services/authentication';
import { AppToaster } from '../../../utils/toaster';

const getErrorMessage = (err: any): string => {
  const gqlMessage =
    err?.graphQLErrors?.[0]?.message ||
    err?.networkError?.result?.errors?.[0]?.message ||
    err?.networkError?.message;
  return gqlMessage || err?.message || 'Something went wrong';
};

const UserPreferences = () => {
  const { t } = useTranslation();
  const user = useContext(UserContext);
  const analytics = useContext(AnalyticsContext);

  const locationsQuery = useQuery(GET_LOCATIONS, {
    selector: 'locations',
    transform: (locations: { code: string; name: string }[]) => {
      return locations.map((location) => ({
        id: location.code,
        value: location.name,
      }));
    },
  });

  const timezonesQuery = useQuery(GET_TIMEZONES, {
    selector: 'timezones',
    transform: (
      timezones: { code: string; name: string; offset: number }[]
    ) => {
      return orderBy(
        timezones.map((timezone) => ({
          id: timezone.code,
          value: `${timezone.code} (UTC ${
            timezone.offset < 0 ? timezone.offset : `+${timezone.offset}`
          })`,
        })),
        ['offset', 'code']
      );
    },
  });

  const languagesQuery = useQuery(GET_ALL_LANGUAGES, {
    selector: 'languages',
    transform: (
      languages: { key: string; name: string; enabled: boolean }[]
    ) => {
      return languages
        .filter((language) => language.enabled)
        .map((language) => ({
          id: language.key,
          value: language.name,
        }));
    },
  });

  const organisationsQuery = useQuery(GET_USER_ORGANISATIONS, {
    selector: 'organisations',
    transform: (organisations: { key: string; name: string }[]) => {
      return orderBy(
        organisations.map((org) => ({
          id: org.key,
          value: org.name?.trim(),
        })),
        (o) => o.value.toLowerCase()
      );
    },
  });

  const [updateUserPreferences] = useMutation(UPDATE_USER_PREFERENCES, {
    refetchQueries: [{ query: GET_CURRENT_USER }],
  });

  const [updateMyPassword] = useMutation(UPDATE_MY_PASSWORD);

  const { loading, error } = useQueryResults({
    languagesQuery,
    organisationsQuery,
    timezonesQuery,
    locationsQuery,
  });

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <Error />;
  }

  return (
    <UserPreferencesComponent
      organisations={organisationsQuery.data}
      languages={languagesQuery.data}
      timezones={timezonesQuery.data}
      // locations={data.locationsQuery}
      firstname={user.firstname}
      lastname={user.lastname}
      preferences={user.preferences || {}}
      showPasswordFields={!authenticationService.requiresSSO()}
      onSavePreferences={async ({
        firstname,
        lastname,
        currentPassword,
        newPassword,
        confirmPassword,
        ...values
      }) => {
        analytics.events.users.userPreferencesSave();

        const hasCurrent =
          typeof currentPassword === 'string' && currentPassword.length > 0;
        const hasNew =
          typeof newPassword === 'string' && newPassword.length > 0;
        const hasConfirm =
          typeof confirmPassword === 'string' && confirmPassword.length > 0;

        try {
          // Password update is optional: only validate + call mutation if user entered something.
          // Do not trim passwords — must match Auth0 and the login form exactly.
          if (hasCurrent || hasNew || hasConfirm) {
            if (!hasCurrent || !hasNew || !hasConfirm) {
              AppToaster.show({
                message: t(
                  `Please enter current password, new password, and confirmation`
                ),
                intent: 'danger',
                icon: 'error',
              });
              return;
            }
            if (newPassword !== confirmPassword) {
              AppToaster.show({
                message: t(`Passwords do not match`),
                intent: 'danger',
                icon: 'error',
              });
              return;
            }

            await updateMyPassword({
              variables: {
                currentPassword,
                newPassword,
              },
            });

            AppToaster.show({
              message: t(`Password updated`),
              intent: 'success',
              icon: 'tick',
            });
          }

          await updateUserPreferences({
            variables: {
              firstname,
              lastname,
              updatedPreferences: {
                ...values,
              },
            },
          });
          AppToaster.show({
            message: t(`Save successful`),
            intent: 'success',
            icon: 'tick',
          });
        } catch (err) {
          AppToaster.show({
            message: t(getErrorMessage(err)),
            intent: 'danger',
            icon: 'error',
          });
        }
      }}
    />
  );
};

export default UserPreferences;
